import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/AuthContext';
import { useTerritory } from '@/contexts/TerritoryContext';
import { Button } from "@/components/ui/button";
import { AppStoreBadge, GooglePlayBadge } from "@/components/AppStoreBadges";
import Header from '@/components/Header';
import Hero from '@/components/Hero';
import Features from '@/components/Features';
import Pricing from '@/components/Pricing';
import Footer from '@/components/Footer';
import { Illustration } from "@/components/Illustration";
import { subjectStyleFor } from "@/lib/subjectStyles";
import { Users, ListChecks, CheckCircle, Gauge, ArrowRight, FileText, Check } from "lucide-react";

/**
 * Real numbers only.
 *
 * This section used to claim "10,000+ Students Enrolled", "95% Parent Satisfaction" and
 * "4.9/5 Average Rating", none of which came from anywhere. The true figures are both verifiable
 * and better: 106,067 learners since December 2011, 232,187 answers recorded, 25,357 published
 * questions. If a number here changes, change it because the database changed.
 */
const PLATFORM_STATS = [
  { value: "106,067", label: "Caribbean learners taught", note: "since 2011" },
  { value: "25,357", label: "Questions in the bank", note: "written to the syllabus" },
  { value: "232,187", label: "Answers recorded", note: "what the national comparison is built on" },
  { value: "13,298", label: "Questions calibrated", note: "measured against real student performance" },
];

function SocialProof() {
  return (
    <section className="py-16 md:py-20 bg-background">
      <div className="container px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">Fourteen years in Caribbean classrooms</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
            Named the number one edtech company in the Caribbean <span className="text-primary">by TIME magazine</span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-base md:text-lg">Endorsed by the Ministry of Education. Teaching Jamaican students online since 2011.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {PLATFORM_STATS.map((stat, i) => {
            const Icon = [Users, ListChecks, CheckCircle, Gauge][i % 4];
            const tone = ["bg-primary", "bg-blue-500", "bg-emerald-500", "bg-amber-500"][i % 4];
            const tint = ["bg-primary/10", "bg-blue-500/10", "bg-emerald-500/10", "bg-amber-500/10"][i % 4];
            return (
              <div key={stat.label} className={`rounded-2xl border border-border ${tint} p-4 md:p-5 flex items-start gap-3`}>
                <div className={`w-11 h-11 rounded-xl ${tone} text-white flex items-center justify-center shrink-0 ${i % 2 ? "rotate-2" : "-rotate-2"} shadow-sm`}><Icon className="w-5 h-5" /></div>
                <div className="min-w-0">
                  <div className="text-2xl md:text-3xl font-bold tabular-nums leading-none">{stat.value}</div>
                  <div className="text-sm font-semibold leading-snug mt-1.5">{stat.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{stat.note}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * The exam ladder. The site sold one exam; the platform now covers three.
 *
 * The primary exam is territory-specific (PEP, SEA, NGSA, GLAT, Common Entrance) and comes from
 * the territory config. CSEC and CAPE are set regionally by CXC, so they read the same in every
 * market we open.
 */
function ExamLadder() {
  const { territory } = useTerritory();

  const rungs = [
    {
      code: territory.examName,
      full: territory.examFullName,
      stage: territory.grades,
      ages: "9 to 12",
      blurb: "Practice, interactive lessons and Performance Tasks in the real exam format.",
      accent: "primary",
      live: true,
    },
    {
      code: "CSEC",
      full: "Caribbean Secondary Education Certificate",
      stage: "Grades 10 and 11",
      ages: "14 to 17",
      blurb: "Twelve subjects, Paper 1 practice and Paper 2 answers marked against the real scheme.",
      accent: "blue",
      live: true,
    },
    {
      code: "CAPE",
      full: "Caribbean Advanced Proficiency Examination",
      stage: "Lower and Upper Sixth",
      ages: "16 to 18",
      blurb: "Fourteen subjects mapped to the CXC modules, with Unit 1 and Unit 2 certified separately.",
      accent: "emerald",
      live: false,
    },
  ];

  return (
    <section className="py-16 md:py-20 bg-primary/5" id="exams">
      <div className="container px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">One platform, the whole way through</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
            From {territory.examName} to CSEC to <span className="text-primary">CAPE</span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto text-base md:text-lg">
            Most children leave a study platform behind the moment the exam is over. Yours does not
            have to. The same account carries them from primary school to sixth form.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {rungs.map((r, i) => {
            const tone = r.accent === "primary" ? { card: "bg-primary/10 border-primary/25", chip: "bg-primary text-primary-foreground" } : r.accent === "blue" ? { card: "bg-blue-500/10 border-blue-500/25", chip: "bg-blue-500 text-white" } : { card: "bg-emerald-500/10 border-emerald-500/25", chip: "bg-emerald-500 text-white" };
            return (
              <div key={r.code} className={`rounded-2xl p-6 border flex flex-col ${tone.card}`} data-testid={`exam-rung-${r.code.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`font-bold text-lg px-3.5 py-1 rounded-xl shadow-sm ${tone.chip} ${i % 2 ? "rotate-1" : "-rotate-1"}`}>{r.code}</span>
                  {!r.live && <span className="text-xs font-semibold text-muted-foreground bg-card border border-border px-2.5 py-1 rounded-full">Opening soon</span>}
                </div>
                <p className="text-sm text-muted-foreground mb-1">{r.full}</p>
                <p className="font-semibold text-sm mb-3">{r.stage} <span className="text-muted-foreground font-normal">· ages {r.ages}</span></p>
                <p className="text-sm leading-relaxed flex-1">{r.blurb}</p>
              </div>
            );
          })}
        </div>

        <p className="text-center text-muted-foreground text-sm mt-8 max-w-2xl mx-auto">
          CSEC and CAPE are set by the Caribbean Examinations Council for the whole region, so they
          are the same exam in every Caribbean country.
        </p>
      </div>
    </section>
  );
}

/** What the platform gained recently and what actually differentiates it. */
function WhatsNew() {
  const items = [
    {
      title: "A free readiness check, marked in minutes",
      body: "Ten real questions from the bank. We mark them, name the two topics to fix first, and show where your child stands against other Caribbean students in the same grade. No account, no payment.",
      href: "/pep-check",
      cta: "Take the free check",
    },
    {
      title: "Performance Tasks marked like the real thing",
      body: "Extended written answers marked against the actual rubric, criterion by criterion, with feedback that names what to change. This is the part of the exam most practice sites skip because it is hard to mark.",
    },
    {
      title: "A national comparison that means something",
      body: "Built from 232,187 answers given by real Caribbean students on this platform, not a textbook average. It is why the report is worth reading.",
    },
    {
      title: "Every question measured, not just written",
      body: "13,298 questions carry a difficulty and discrimination score fitted from how students actually answered them, so practice targets what is genuinely costing marks.",
    },
  ];

  return (
    <section className="py-16 md:py-20 bg-background">
      <div className="container px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">What makes this different</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
            Marking, not just <span className="text-primary">more questions</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <div key={it.title} className="rounded-2xl border border-border bg-card p-6 md:p-7 flex gap-4 shadow-sm">
              <div className={`w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 font-bold ${i % 2 ? "rotate-2" : "-rotate-2"} shadow-sm`}>{i + 1}</div>
              <div className="min-w-0">
                <h3 className="font-bold text-lg mb-1.5">{it.title}</h3>
                <p className="text-muted-foreground text-sm md:text-base leading-relaxed">{it.body}</p>
                {it.href && (
                  <a href={it.href} className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-primary hover:underline" data-testid="link-whats-new-cta">{it.cta}<ArrowRight className="h-4 w-4" /></a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PracticePapersPromo() {
  return (
    <section className="py-16 md:py-20 bg-primary/5">
      <div className="container px-4 md:px-6 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">Practice papers</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Print-ready <span className="text-primary">PEP practice papers</span>
            </h2>
            <p className="text-muted-foreground text-base md:text-lg mb-6 leading-relaxed">
              Professionally formatted practice papers that match the real PEP exam. 40 curriculum-aligned questions per paper with diagrams, answer keys, and Jamaican context throughout.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Grades 4, 5, and 6 across all subjects",
                "Multiple choice with diagrams and scenario questions",
                "Separate answer key included",
                "No account needed. Select, pay, download.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0"><Check className="h-3 w-3" strokeWidth={3} /></span>
                  <span className="text-sm md:text-base">{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              <a href="/practice-papers" className="inline-flex items-center gap-2 h-12 px-6 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary/90 transition-colors text-sm md:text-base">
                Browse papers<ArrowRight className="h-4 w-4" />
              </a>
              <span className="inline-flex items-center text-sm text-muted-foreground font-medium">From J$350 per paper</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { grade: 4, subject: "Mathematics" },
              { grade: 5, subject: "Language Arts" },
              { grade: 6, subject: "Science" },
              { grade: 4, subject: "Social Studies" },
            ].map((paper, i) => {
              const st = subjectStyleFor(paper.subject);
              return (
                <div key={i} className={`rounded-xl border p-4 ${st.card} hover:-translate-y-0.5 hover:shadow-md transition-all`}>
                  <div className={`w-10 h-10 rounded-xl ${st.sticker} flex items-center justify-center mb-3 ${i % 2 ? "rotate-2" : "-rotate-2"} shadow-sm`}><FileText className="h-5 w-5" /></div>
                  <p className="font-semibold text-sm">Grade {paper.grade}</p>
                  <p className="text-xs text-muted-foreground">{paper.subject}</p>
                  <p className="text-xs text-muted-foreground mt-1">40 questions</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  const { territory } = useTerritory();

  return (
    <section className="py-20 md:py-28 bg-primary text-primary-foreground">
      <div className="container px-4 md:px-6 max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
          <Illustration name="owl-cheer" size={180} className="shrink-0" />
          <div className="text-center md:text-left flex-1">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Ready to give your child <span className="text-amber-300">the best start?</span>
            </h2>
            <p className="text-lg md:text-xl text-primary-foreground/85 max-w-2xl mb-8 leading-relaxed">{territory.cta.description}</p>
            <div className="flex flex-col sm:flex-row items-center md:justify-start justify-center gap-3">
              <Button size="lg" className="h-14 px-8 text-base rounded-xl bg-amber-400 text-amber-950 hover:bg-amber-300 font-bold" onClick={() => window.location.href = '/signup'}>Get started</Button>
              <Button size="lg" variant="secondary" className="h-14 px-8 text-base rounded-xl bg-white/15 hover:bg-white/25 text-primary-foreground font-semibold" onClick={() => window.location.href = '/contact'}>Text us</Button>
            </div>
            <p className="text-primary-foreground/60 text-sm mt-4">Cancel anytime.</p>
            <div className="flex items-center md:justify-start justify-center gap-3 mt-6">
              <AppStoreBadge tone="dark" />
              <GooglePlayBadge tone="dark" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { isAuthenticated, isLoading } = useAuth();
  const { territory } = useTerritory();
  const [, setLocation] = useLocation();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      setLocation('/dashboard');
    }
  }, [isAuthenticated, isLoading, setLocation]);

  // Update SEO meta tags based on territory
  useEffect(() => {
    document.title = territory.seo.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', territory.seo.description);
  }, [territory]);

  // Show loading or nothing while checking auth
  if (isLoading) {
    return null;
  }

  // Only show marketing site to non-authenticated users
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <Hero />
        <SocialProof />
        <ExamLadder />
        <WhatsNew />
        <Features />
        <PracticePapersPromo />
        <Pricing />
        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}
